import { z } from 'zod';
import { rcrmCustomFieldWriteSchema, rcrmRecordTypeSchema } from './common.js';
/**
 * Request bodies the CRM sends to RecruitCRM. Every write is a `POST`: create is
 * `POST /v1/{entity}`, update is `POST /v1/{entity}/{slug}` (notes: `/v1/notes/{id}`). Only the
 * fields the TS-1831 field maps send are listed; all are optional here because an update sends
 * only what changed. Required-on-create rules are RCRM's (see `apps/rcrm-mock`).
 */
const customFields = z.array(rcrmCustomFieldWriteSchema).optional();
/** RCRM user id (resolved from the CRM user's email via `GET /v1/users`). */
const userId = z.number().int();
export const rcrmCandidateWriteSchema = z.looseObject({
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string().optional(),
    contact_number: z.string().optional(),
    /** 1 male, 2 female, 3 other; 0 clears (always sent with `contact_gender` and cf24). */
    gender_id: z.number().int().optional(),
    contact_gender: z.string().optional(),
    position: z.string().optional(),
    current_organization: z.string().optional(),
    work_ex_year: z.number().optional(),
    locality: z.string().optional(),
    country: z.string().optional(),
    source: z.string().optional(),
    owner_id: userId.optional(),
    custom_fields: customFields,
});
export const rcrmContactWriteSchema = z.looseObject({
    first_name: z.string().optional(),
    /** Required by RCRM: the CRM sends `" "` (one space) when it has none. */
    last_name: z.string().optional(),
    email: z.string().optional(),
    contact_number: z.string().optional(),
    designation: z.string().optional(),
    company_slug: z.string().optional(),
    owner_id: userId.optional(),
    custom_fields: customFields,
});
export const rcrmCompanyWriteSchema = z.looseObject({
    company_name: z.string().optional(),
    website: z.string().optional(),
    country: z.string().optional(),
    owner_id: userId.optional(),
    custom_fields: customFields,
});
export const rcrmJobWriteSchema = z.looseObject({
    name: z.string().optional(),
    company_slug: z.string().optional(),
    contact_slug: z.string().optional(),
    /** HTML. */
    job_description_text: z.string().optional(),
    /** The status id (`0` = Closed is a real id). */
    job_status: z.number().int().optional(),
    number_of_openings: z.number().int().optional(),
    /** `1` on create so RCRM returns `application_form_url` (Relay). */
    enable_job_application_form: z.union([z.literal(0), z.literal(1)]).optional(),
    owner_id: userId.optional(),
    custom_fields: customFields,
});
/**
 * A deal update (`POST /v1/deals/{slug}`, "Edit a deal"): the CRM sends the stage ONLY (TS-1878).
 * The docs mark `name`, `deal_value` and `close_date` as required on edit too; production accepted
 * `{ deal_stage }` alone (a write on the designated test deal, 2026-10-31), answering 200 with the
 * whole deal (`docs/nitpicks/recruitcrm/deal-stage-write.md`). Resending the same stage is a 200
 * too, not the no-change 422. Deals are never created by the CRM.
 */
export const rcrmDealWriteSchema = z.looseObject({
    /** The stage id from `GET /v1/deals-pipeline` (a number; 0 would be a real id). */
    deal_stage: z.number().int().optional(),
});
export const rcrmNoteCreateSchema = z.looseObject({
    /** HTML, at most 10,000 characters (`RCRM_NOTE_MAX_CHARS`). */
    description: z.string(),
    /** The related record's slug. */
    related_to: z.string(),
    related_to_type: rcrmRecordTypeSchema,
    created_by: userId.optional(),
});
/** Editing a note sends only its text. */
export const rcrmNoteUpdateSchema = z.looseObject({ description: z.string() });
//# sourceMappingURL=writes.js.map